import { useState, useEffect, useMemo } from 'react';
import EventTableView from './EventTableView.js';
import EventCardView from './EventCardView.js';
import EventCalendarView from './EventCalendarView.js';
import EventFilter from './EventFilter.js';
import { useData } from '../context/DataContext.js';
import { 
  FunnelIcon, 
  TableCellsIcon, 
  Squares2X2Icon,
  CalendarIcon 
} from '@heroicons/react/24/outline';
import PartnerCarousel from './PartnerCarousel.js';


export default function Events({ onOpenLogin, onEventAdded, initialPromoter }) {
  const { events: countryFilteredEvents, partners, isLoading, error, selectedCountry } = useData();
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [viewMode, setViewMode] = useState('card');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(null);
  const [hasInitialPromoter, setHasInitialPromoter] = useState(false); // Start as false, will be set to true when filter is applied

  // Initialize filteredEvents when events are loaded (only if no filters are active and no initialPromoter waiting)
  useEffect(() => {
    // Only set all events if:
    // 1. We have events
    // 2. No active filters are set
    // 3. No initialPromoter is waiting to be filtered (if initialPromoter exists, let its useEffect handle filtering)
    if (countryFilteredEvents && !activeFilters && !initialPromoter) {
      setFilteredEvents(countryFilteredEvents);
    }
  }, [countryFilteredEvents, activeFilters, initialPromoter]);

  // Reset filters and close filter panel when country changes (but not on initial load)
  useEffect(() => {
    // Only reset if we don't have an initialPromoter to apply
    if (!initialPromoter) {
      setActiveFilters(null);
      setFilteredEvents(countryFilteredEvents || []);
      setHasInitialPromoter(false);
    }
    //setIsFilterOpen(false);
  }, [selectedCountry, initialPromoter, countryFilteredEvents]);

  // Use useMemo for activePartners
  const activePartners = useMemo(() => {
    return partners ? partners.filter(partner => partner.isPaid === 1) : [];
  }, [partners]);

  const handleFilterApply = (selectedFilters) => {
    // If clearing filters and we had an initialPromoter, clear the URL parameter
    if (!selectedFilters && hasInitialPromoter) {
      const url = new URL(window.location);
      url.searchParams.delete('promoter');
      window.history.replaceState({}, '', url);
      setHasInitialPromoter(false);
    }
    
    // Clear URL parameter if promoter filter is cleared from selectedFilters
    if (selectedFilters && hasInitialPromoter && selectedFilters.promoter.length === 0) {
      const url = new URL(window.location);
      url.searchParams.delete('promoter');
      window.history.replaceState({}, '', url);
      setHasInitialPromoter(false);
    }
    
    setActiveFilters(selectedFilters);
    
    if (!selectedFilters || !countryFilteredEvents) {
      setFilteredEvents(countryFilteredEvents || []);
      return;
    }

    let filtered = [...countryFilteredEvents];

    // Apply date range filter
    if (selectedFilters.dateRange.start && selectedFilters.dateRange.end) {
      filtered = filtered.filter(event => {
        if (!event.StartDateTime) return false;
        
        const eventDate = new Date(event.StartDateTime);
        const startDate = new Date(selectedFilters.dateRange.start);
        const endDate = new Date(selectedFilters.dateRange.end);
        
        // Set time to start and end of day for proper comparison
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        
        return eventDate >= startDate && eventDate <= endDate;
      });
    }

    // Apply other filters if they have selected values
    if (selectedFilters.state.length > 0) {
      filtered = filtered.filter(event => 
        selectedFilters.state.includes(event.StateNameShort)
      );
    }

    if (selectedFilters.venue.length > 0) {
      filtered = filtered.filter(event => 
        selectedFilters.venue.includes(event.VenueName)
      );
    }

    if (selectedFilters.promoter.length > 0) {
      filtered = filtered.filter(event => 
        selectedFilters.promoter.includes(event.BusinessName)
      );
    }

    if (selectedFilters.type.length > 0) {
      filtered = filtered.filter(event => 
        selectedFilters.type.includes(event.EventType)
      );
    }

    setFilteredEvents(filtered);
  };

  // Initialize filters with promoter if initialPromoter is present (only once)
  useEffect(() => {
    // Only apply initialPromoter filter if:
    // 1. We have initialPromoter
    // 2. Events are loaded
    // 3. No active filters are set yet
    // 4. We haven't already processed the initialPromoter (hasInitialPromoter is false)
    // 5. The URL still has the promoter parameter (to prevent re-applying after clear)
    const urlParams = new URLSearchParams(window.location.search);
    const urlPromoter = urlParams.get('promoter');
    
    // Check if URL has promoter parameter (to allow initial load, but prevent re-apply after clear)
    // Compare decoded values to handle URL encoding
    const decodedUrlPromoter = urlPromoter ? decodeURIComponent(urlPromoter) : null;
    const shouldApplyInitialPromoter = initialPromoter && 
                                       countryFilteredEvents && 
                                       countryFilteredEvents.length > 0 && 
                                       activeFilters === null && 
                                       !hasInitialPromoter &&
                                       decodedUrlPromoter && 
                                       decodedUrlPromoter.toLowerCase() === initialPromoter.toLowerCase(); // URL parameter matches the prop
    
    if (shouldApplyInitialPromoter) {
      // Decode the promoter name from URL
      const decodedPromoter = decodeURIComponent(initialPromoter);
      
      // Find the actual BusinessName that matches (case-insensitive)
      const matchingPromoter = countryFilteredEvents.find(event => 
        event.BusinessName && event.BusinessName.toLowerCase() === decodedPromoter.toLowerCase()
      );
      
      if (matchingPromoter) {
        const initialFilters = {
          dateRange: { start: null, end: null },
          state: [],
          venue: [],
          promoter: [matchingPromoter.BusinessName], // Use the actual BusinessName from the event
          type: []
        };
        setActiveFilters(initialFilters);
        setHasInitialPromoter(true);
        // Apply the filter immediately
        const filtered = countryFilteredEvents.filter(event => 
          event.BusinessName === matchingPromoter.BusinessName
        );
        setFilteredEvents(filtered);
      }
    }
  }, [initialPromoter, countryFilteredEvents, activeFilters, hasInitialPromoter]);

  if (isLoading) return <p>Loading events...</p>;
  if (error) return <p>Error loading events: {error.message}</p>;

  return (
    <div className="relative isolate px-4 pt-14 lg:px-8 h-full ">
      <div className="max-w-7xl mx-auto ">
        <div className="fixed top-[64px] left-0 right-0 z-50 md:justify-between flex flex-col md:flex-row gap-1 md:gap-0 pointer-events-none bg-slate-900/35">
          <div className="max-w-7xl w-full mx-auto px-2 ">
            {/* Mobile Layout */}
            <div className="md:hidden flex flex-col gap-1 pointer-events-auto py-1 pb-0">
              {/* Top row with filters and view toggle */}
              {/* Partner Images Carousel - only show when not in grid/card view */}
              {viewMode !== 'card' && <PartnerCarousel partners={activePartners} />}
              
              <div className="flex justify-between gap-2">
                {/* Filter button */}
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={`flex flex-col items-center rounded-md px-3 py-1 text-sm border border-gray-200 
                    h-[60px] w-[120px] justify-center shadow-md
                    ${isFilterOpen ? 'bg-slate-900 text-white border-transparent' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  <FunnelIcon className="h-5 w-5" />
                  <span className="text-[10px] mt-1">Filters</span>
                </button>

                {/* Mobile View toggles */}
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 h-[60px] w-[120px] shadow-md">
                  <button
                    onClick={() => setViewMode('calendar')}
                    className={`flex flex-col items-center rounded-md px-3 py-1 text-sm w-[58px]
                      ${viewMode === 'calendar' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <CalendarIcon className="h-5 w-5" />
                    <span className="text-[10px] mt-1">Calendar</span>
                  </button>
                  <button
                    onClick={() => setViewMode('card')}
                    className={`flex flex-col items-center rounded-md px-3 py-1 text-sm w-[58px]
                      ${viewMode === 'card' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Squares2X2Icon className="h-5 w-5" />
                    <span className="text-[10px] mt-1">Grid</span>
                  </button>
                </div>
              </div>

 
            </div>

            {/* Desktop Layout */}
            <div className="hidden md:flex md:flex-row justify-between items-center pointer-events-auto py-2">
              {/* Filter button */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className={`flex flex-col items-center rounded-md px-3 py-1 text-sm border border-gray-200 
                  h-[58px] w-[180px] justify-center shadow-md
                  ${isFilterOpen ? 'bg-slate-900 text-white border-transparent' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                <FunnelIcon className="h-5 w-5" />
                <span className="text-[10px] mt-1">Filters</span>
              </button>

              {/* Partner Images Carousel - only show when not in grid/card view */}
              {viewMode !== 'card' && <PartnerCarousel partners={activePartners} />}

              {/* Desktop View toggles */}
              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 h-[58px] w-[180px] shadow-md">
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`flex flex-col items-center rounded-md px-3 py-1 text-sm w-[58px]
                    ${viewMode === 'calendar' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <CalendarIcon className="h-5 w-5" />
                  <span className="text-[10px] mt-1">Calendar</span>
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`flex flex-col items-center rounded-md px-3 py-1 text-sm w-[58px]
                    ${viewMode === 'table' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <TableCellsIcon className="h-5 w-5" />
                  <span className="text-[10px] mt-1">List</span>
                </button>
                <button
                  onClick={() => setViewMode('card')}
                  className={`flex flex-col items-center rounded-md px-3 py-1 text-sm w-[58px]
                    ${viewMode === 'card' ? 'bg-slate-900 text-white' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <Squares2X2Icon className="h-5 w-5" />
                  <span className="text-[10px] mt-1">Grid</span>
                </button>
              </div>
            </div>
          </div>
        </div>
  
        <div className="mt-4 md:mt-8">
          <EventFilter
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onFilterApply={handleFilterApply}
            activeFilters={activeFilters}
          />

          {/* Desktop views */}
          <div className="hidden md:block">
            {viewMode === 'table' ? (
              <EventTableView 
                events={filteredEvents} 
                onOpenLogin={(message) => onOpenLogin(message)}
                onEventAdded={onEventAdded}
              />
            ) : viewMode === 'calendar' ? (
              <EventCalendarView 
                events={filteredEvents}
                onOpenLogin={onOpenLogin}
                onEventAdded={onEventAdded}
              />
            ) : (
              <EventCardView 
                events={filteredEvents}
                onOpenLogin={(message) => onOpenLogin(message)}
                onEventAdded={onEventAdded}
              />
            )}
          </div>

          {/* Mobile views */}
          <div className="md:hidden">
            {viewMode === 'calendar' ? (
              <EventCalendarView 
                events={filteredEvents}
                onOpenLogin={onOpenLogin}
                onEventAdded={onEventAdded}
              />
            ) : (
              <EventCardView 
                events={filteredEvents}
                onOpenLogin={(message) => onOpenLogin(message)}
                onEventAdded={onEventAdded}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 
